import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  MessageCircle,
  Users,
  HelpCircle,
  Info,
  Sparkles,
  ShieldAlert,
  Settings2,
  X,
} from "lucide-react";
import { getAnalysis, getTranscript } from "@/state/transcriptStore";
import { UtteranceTags } from "@/components/UtteranceTags";
import { useEffect, useMemo, useState } from "react";
import {
  loadViewPrefs,
  type ViewPrefs,
  EVT,
  KEY,
  saveViewPrefs,
} from "@/state/viewPrefs";
import { fingerprintTranscript } from "@ayla/conversation";
// EncounterSnapshot import removed - not used
import { DeltaBadge } from "@/components/DeltaBadge";
import {
  HISTORY_EVT,
  HISTORY_KEY,
  HISTORY_PING_KEY,
  rollingAvg,
  pushInterruptionCount,
  getInterruptionAverage,
} from "@/utils/history";
import { CURRENT_PING_KEY, CURRENT_CLEARED_EVT } from "@/state/transcriptStore";
import { Heart } from "lucide-react";
import { fmt } from "@ayla/conversation";

export function FeedbackCard() {
  // ============================================================================
  // STATE & DATA
  // ============================================================================
  const analysis = getAnalysis();
  const tx = getTranscript();
  const [tick, setTick] = useState(0);
  const [prefs, setPrefs] = useState<ViewPrefs>(() => loadViewPrefs());
  const [showTranscript, setShowTranscript] = useState(false);
  const [collapsedInterruptions, setCollapsedInterruptions] = useState(false);
  const [collapsedEmpathy, setCollapsedEmpathy] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const avg3 = useMemo(() => {
    void tick;
    return rollingAvg(3, tx ? fingerprintTranscript(tx) : undefined);
  }, [tick, tx]);

  const iAvg = useMemo(() => {
    void tick;
    return avg3?.interruptionsCount ?? getInterruptionAverage();
  }, [avg3, tick]);

  const iDeltaPct = useMemo(() => {
    if (iAvg == null || iAvg === 0) return null;
    const current = analysis?.interruptions?.count ?? 0;
    return ((current - iAvg) / iAvg) * 100;
  }, [iAvg, analysis?.interruptions?.count]);

  const iDeltaLabel = useMemo(() => {
    if (iDeltaPct == null) return null;
    const up = iDeltaPct > 0;
    const pctAbs = Math.abs(iDeltaPct).toFixed(0);
    return `${up ? "▲" : "▼"} ${pctAbs}% vs. avg`;
  }, [iDeltaPct]);

  // ============================================================================
  // EFFECTS - Snapshot Management
  // ============================================================================
  useEffect(() => {
    const count = analysis?.interruptions?.count;
    if (typeof count === "number") {
      pushInterruptionCount(count);
    }
  }, [analysis?.interruptions?.count]);

  // ============================================================================
  // EFFECTS - Event Listeners
  // ============================================================================
  useEffect(() => {
    const onHist = () => setTick(t => t + 1);
    const onPrefs = (e: Event) =>
      setPrefs((e as CustomEvent<ViewPrefs>).detail);
    const onCurrentCleared = () => setTick(t => t + 1);
    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return;

      if (e.key === KEY) {
        setPrefs(loadViewPrefs());
      } else if (
        [HISTORY_KEY, HISTORY_PING_KEY, CURRENT_PING_KEY].includes(e.key!)
      ) {
        setTick(t => t + 1);
      }
    };

    window.addEventListener(EVT, onPrefs);
    window.addEventListener(HISTORY_EVT, onHist);
    window.addEventListener(CURRENT_CLEARED_EVT, onCurrentCleared);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener(EVT, onPrefs);
      window.removeEventListener(HISTORY_EVT, onHist);
      window.removeEventListener(CURRENT_CLEARED_EVT, onCurrentCleared);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const togglePref = (key: keyof ViewPrefs) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    saveViewPrefs(newPrefs);
  };

  // ============================================================================
  // EARLY RETURN & RENDER CONSTANTS
  // ============================================================================
  if (!analysis || !tx) return null;

  const data = [
    { name: "Clinician", value: analysis.talkTime.clinicianPct },
    { name: "Patient", value: analysis.talkTime.patientPct },
  ];
  const COLORS = ["hsl(var(--primary))", "hsl(var(--success))"];
  const totalQuestions = analysis.questions.open + analysis.questions.closed;
  const coach = analysis.coach;

  const sections = [
    { key: "showCoachNotes", label: "Coach notes", icon: Sparkles },
    { key: "showTalkTime", label: "Talk time", icon: Users },
    { key: "showQuestionMix", label: "Question mix", icon: HelpCircle },
    { key: "showInterruptions", label: "Interruptions", icon: ShieldAlert },
    { key: "showEmpathy", label: "Empathy indicators", icon: Heart },
    {
      key: "showTranscriptTags",
      label: "Transcript tags",
      icon: MessageCircle,
    },
  ];

  return (
    <div className="relative max-w-4xl mx-auto">
      <Card className="shadow-lg border bg-card">
        <CardHeader className="pb-4 bg-muted/30 rounded-t-lg border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-xl font-bold text-foreground">
              <MessageCircle className="w-5 h-5 text-primary" />
              Encounter Feedback
            </CardTitle>
            <button
              onClick={() => setShowCustomize(!showCustomize)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <Settings2 className="w-4 h-4" />
              Customize
            </button>
          </div>
        </CardHeader>

        {/* Customize Popup */}
        {showCustomize && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setShowCustomize(false)}
            />

            {/* Popup */}
            <div className="absolute right-4 top-20 z-50 w-80 bg-card border rounded-lg shadow-2xl">
              <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                <div className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-primary" />
                  <h3 className="font-bold text-sm">Customize View</h3>
                </div>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="p-1 hover:bg-muted rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-4 space-y-1 max-h-96 overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-3">
                  Toggle sections to customize your feedback view
                </p>

                {sections.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => togglePref(key as keyof ViewPrefs)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${
                      prefs[key as keyof ViewPrefs]
                        ? "bg-primary/10 hover:bg-primary/20 border border-primary/20"
                        : "hover:bg-muted border border-transparent"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        className={`w-4 h-4 ${prefs[key as keyof ViewPrefs] ? "text-primary" : "text-muted-foreground"}`}
                      />
                      <span
                        className={`text-sm font-medium ${prefs[key as keyof ViewPrefs] ? "text-foreground" : "text-muted-foreground"}`}
                      >
                        {label}
                      </span>
                    </div>
                    <div
                      className={`w-10 h-5 rounded-full transition-colors ${
                        prefs[key as keyof ViewPrefs]
                          ? "bg-primary"
                          : "bg-muted"
                      } relative`}
                    >
                      <div
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                          prefs[key as keyof ViewPrefs]
                            ? "translate-x-5"
                            : "translate-x-0.5"
                        }`}
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        <CardContent className="space-y-8 p-6">
          {/* Coach Notes Section (toggle) */}
          {prefs.showCoachNotes && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-foreground">
                  Coach notes (for your eyes only)
                </h3>
              </div>

              <div className="bg-card rounded-xl shadow-sm border p-6 space-y-6">
                {/* Strengths */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <div className="text-sm font-semibold">What went well</div>
                  </div>
                  {coach?.strengths?.length ? (
                    <ul className="space-y-2 ml-4">
                      {coach.strengths
                        .slice(0, 3)
                        .map((s: string, i: number) => (
                          <li
                            key={`${s}-${i}`}
                            className="text-sm text-muted-foreground flex items-start gap-2"
                          >
                            <span className="text-green-500 mt-0.5">•</span>
                            <span>{s}</span>
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-muted-foreground ml-4">
                      No highlights yet—these will appear here.
                    </div>
                  )}
                </div>

                {/* Opportunity (single) */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <div className="text-sm font-semibold">
                      One thing to try next time
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground ml-4">
                    {(coach?.opportunity ?? coach?.opportunities?.[0]) ||
                      "We'll suggest one here once we have enough context."}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Empathy examples */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <div className="text-sm font-semibold">
                        Supportive phrases
                      </div>
                    </div>
                    {coach?.empathy_examples?.length ? (
                      <div className="space-y-2">
                        {coach.empathy_examples.map((e: string, i: number) => (
                          <div
                            key={`${e}-${i}`}
                            className="px-3 py-2 bg-purple-50 rounded-md border border-purple-100"
                          >
                            <span className="text-xs text-purple-900">
                              "{e}"
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        You'll see short, supportive phrases here.
                      </div>
                    )}
                  </div>

                  {/* Reflective prompt */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="text-sm font-semibold">
                        A question to consider
                      </div>
                    </div>
                    <div className="text-sm italic text-muted-foreground">
                      {coach?.reflective_prompt ||
                        "We'll add a reflection prompt after we process more context."}
                    </div>
                  </div>
                </div>

                <div className="text-[11px] text-muted-foreground/60 pt-2 border-t border-border/50">
                  🔒 Private to your eyes only
                </div>
              </div>
            </div>
          )}

          {/* Talk Time Section (toggle) */}
          {prefs.showTalkTime && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-foreground">
                  Talk Time Distribution
                </h3>
              </div>
              <div className="text-[11px] text-muted-foreground -mt-2 mb-2">
                {avg3
                  ? `Comparing to your last ${avg3.count} encounter${avg3.count > 1 ? "s" : ""}.`
                  : "Analyze a few more encounters to build your personal trendline."}
              </div>

              <div className="bg-card rounded-xl shadow-sm border p-4">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  >
                    <Pie
                      data={data}
                      dataKey="value"
                      outerRadius={85}
                      innerRadius={40}
                      paddingAngle={2}
                      label={({ name, percent }) =>
                        `${name}: ${Math.round(percent * 100)}%`
                      }
                      labelLine={false}
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={COLORS[index % COLORS.length]}
                          stroke="hsl(var(--background))"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {[
                {
                  label: "Clinician",
                  color: "bg-primary",
                  value: analysis.talkTime.clinicianPct,
                  textColor: "text-primary",
                  avg: avg3?.talkClinicianPct,
                  showDelta: true,
                },
                {
                  label: "Patient",
                  color: "bg-success",
                  value: analysis.talkTime.patientPct,
                  textColor: "text-success",
                  avg: avg3?.talkPatientPct,
                  showDelta: false,
                },
              ].map(item => (
                <div
                  key={item.label}
                  className="flex justify-between items-center bg-muted rounded-lg p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full ${item.color}`} />
                    <span className="font-semibold text-foreground">
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className={`text-2xl font-bold ${item.textColor}`}>
                      {item.value}%
                    </span>
                    {item.showDelta && (
                      <DeltaBadge
                        value={item.value}
                        avg={item.avg}
                        label=""
                        className="ml-2"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Question Mix Section (toggle) */}
          {prefs.showQuestionMix && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <HelpCircle className="w-5 h-5 text-success" />
                <h3 className="text-lg font-bold text-foreground">
                  Question Analysis
                </h3>
              </div>

              <div className="bg-card rounded-xl shadow-sm border p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-primary/10 rounded-lg">
                    <div className="text-3xl font-bold text-primary">
                      {analysis.questions.open}
                    </div>
                    <div className="text-sm font-semibold text-primary">
                      Open Questions
                    </div>
                  </div>

                  <div className="text-center p-4 bg-muted rounded-lg">
                    <div className="text-3xl font-bold text-foreground">
                      {analysis.questions.closed}
                    </div>
                    <div className="text-sm font-semibold text-muted-foreground">
                      Closed Questions
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {avg3 && (
                    <div className="text-[11px] text-muted-foreground -mt-1 mb-2">
                      Comparing to your last {avg3.count} encounter
                      {avg3.count > 1 ? "s" : ""}.
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-foreground">
                      Open Question Ratio
                    </span>
                    <div className="flex items-center">
                      <span className="text-lg font-bold text-primary">
                        {analysis.questions.openPct}%
                      </span>
                      <DeltaBadge
                        value={analysis.questions.openPct}
                        avg={avg3?.openPct}
                        label=""
                      />
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-3">
                    <div
                      className="bg-primary h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${analysis.questions.openPct}%` }}
                    />
                  </div>
                </div>

                <div className="text-xs text-muted-foreground text-center pt-2">
                  Total: {totalQuestions} questions asked
                </div>
              </div>

              <div className="bg-muted/40 border rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-foreground">
                    <div className="font-semibold mb-1">
                      Question Classification:
                    </div>
                    <div className="space-y-1">
                      <div>
                        <span className="font-medium">Open:</span> WH questions,
                        "tell me about", "walk me through", "describe"
                      </div>
                      <div>
                        <span className="font-medium">Closed:</span> Yes/no
                        questions and specific queries
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Interruptions Section */}
          {prefs.showInterruptions && analysis.interruptions && (
            <div className="space-y-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() =>
                  setCollapsedInterruptions(!collapsedInterruptions)
                }
              >
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-amber-600" />
                  <h3 className="text-lg font-bold text-foreground">
                    Interruptions
                  </h3>
                </div>
                <span className="text-sm text-muted-foreground">
                  {collapsedInterruptions ? "Expand ▼" : "Collapse ▲"}
                </span>
              </div>

              {!collapsedInterruptions && (
                <div className="bg-card rounded-xl shadow-sm border p-4 space-y-3">
                  <div className="text-sm">
                    You interrupted the patient:{" "}
                    <span className="font-semibold">
                      {analysis.interruptions.count}
                    </span>{" "}
                    {analysis.interruptions.count === 1 ? "time" : "times"}
                  </div>

                  {typeof iAvg === "number" && (
                    <div className="text-sm text-muted-foreground">
                      Your avg:{" "}
                      <span className="font-medium">{iAvg.toFixed(1)}</span>{" "}
                      {iDeltaPct != null && (
                        <span
                          className={`ml-1 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs border ${
                            iDeltaPct < 0
                              ? "border-emerald-200 text-emerald-700 dark:border-emerald-900/40 dark:text-emerald-300"
                              : "border-rose-200 text-rose-800 dark:border-rose-900/40 dark:text-rose-300"
                          }`}
                          title="Change vs. your rolling average"
                        >
                          [{iDeltaLabel}]
                        </span>
                      )}
                    </div>
                  )}

                  {(analysis.interruptions.timestamps?.length ?? 0) > 0 && (
                    <div className="text-sm">
                      <div className="font-medium mb-1">
                        When interruptions occurred:
                      </div>
                      <ul className="list-disc ml-5 space-y-1">
                        {analysis.interruptions.timestamps.map((t, i) => (
                          <li key={i}>
                            <span className="font-medium">{fmt(t)}</span>
                            {analysis.interruptions.context?.[i] && (
                              <>
                                {" "}
                                —{" "}
                                <span className="text-muted-foreground">
                                  {analysis.interruptions.context[i]}
                                </span>
                              </>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="text-[12px] text-muted-foreground">
                    💡 Tip: Letting patients finish often improves satisfaction
                    and rapport.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empathy Indicators */}
          {prefs.showEmpathy && analysis.empathy && (
            <div className="space-y-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setCollapsedEmpathy(!collapsedEmpathy)}
              >
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-foreground">
                    Empathy Indicators
                  </h3>
                </div>
                <span className="text-sm text-muted-foreground">
                  {collapsedEmpathy ? "Expand ▼" : "Collapse ▲"}
                </span>
              </div>

              {!collapsedEmpathy && (
                <div className="bg-card rounded-xl shadow-sm border p-4 space-y-4">
                  <div className="text-sm">
                    Empathetic phrases detected:{" "}
                    <span className="font-semibold">
                      {analysis.empathy.count ?? 0}
                    </span>
                  </div>

                  {(analysis.empathy.indicators?.length ?? 0) > 0 && (
                    <div className="text-sm">
                      <div className="font-medium mb-1">
                        Examples from this encounter:
                      </div>
                      <ul className="list-disc ml-5 space-y-1">
                        {analysis.empathy.indicators.map((phrase, i) => {
                          const ts = analysis.empathy.moments?.[i];
                          return (
                            <li key={i}>
                              "{phrase}"
                              {typeof ts === "number" && (
                                <span> ({fmt(ts)})</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {analysis.teachBack && (
                    <div className="text-sm">
                      <div className="font-medium mb-1">
                        ✅ Teach-back moments: {analysis.teachBack.count ?? 0}
                      </div>
                      {(analysis.teachBack.moments?.length ?? 0) > 0 && (
                        <ul className="list-disc ml-5 space-y-1">
                          {analysis.teachBack.moments.map((t, i) => (
                            <li key={i}>
                              ({fmt(t)})
                              {analysis.teachBack.quotes?.[i] && (
                                <> — "{analysis.teachBack.quotes[i]}"</>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Transcript Tags Section */}
          {prefs.showTranscriptTags && (
            <div className="space-y-2">
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className="text-sm font-semibold text-primary hover:underline"
              >
                {showTranscript
                  ? "Hide Transcript Patterns"
                  : "Show Transcript Patterns"}
              </button>

              {showTranscript && (
                <div className="space-y-4 mt-2">
                  {getTranscript()?.entries.map(
                    (
                      e: {
                        speaker?: "clinician" | "patient" | "other";
                        text?: string;
                        start?: number;
                        end?: number;
                      },
                      i: number
                    ) => {
                      const tagInfo = analysis.tags?.find(t => t.index === i);
                      if (e.speaker !== "clinician") return null;
                      return (
                        <div
                          key={i}
                          className="p-2 border rounded-md bg-muted/20"
                        >
                          <div className="font-semibold">{e.speaker}</div>
                          <div>{e.text}</div>
                          {tagInfo && <UtteranceTags tags={tagInfo.tags} />}
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
